/**
 * TimeSeriesChart — ECharts wrapper for queue time time-series visualization.
 * Displays queue time data as line chart with optional threshold overlay lines.
 * Supports single-line (data prop) or multi-line (lines prop) mode.
 */
import React from 'react';
import ReactECharts from 'echarts-for-react';

export interface ChartDataLine {
  attractionId: string;
  attractionName: string;
  data: Array<{ timestamp: string; waitTimeMinutes: number }>;
  color?: string;
}

export interface TimeSeriesChartProps {
  data?: Array<{ timestamp: string; waitTimeMinutes: number }>;
  lines?: ChartDataLine[];
  thresholds?: Record<string, { walkOn: number; shortWait: number; moderateWait: number; longWait: number }>;
  singleThreshold?: { walkOn: number; shortWait: number; moderateWait: number; longWait: number };
  isLoading?: boolean;
  title?: string;
}

const LINE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _buildMarkLines = () => undefined;

function getEChartsOption(
  data: TimeSeriesChartProps['data'],
  lines: TimeSeriesChartProps['lines'],
  thresholds: TimeSeriesChartProps['thresholds'],
  singleThreshold: TimeSeriesChartProps['singleThreshold'],
  title: string,
): any {
  // MarkLine rendering was removed — see buildMarkLines() for rationale.
  // Threshold info is still visible on the DistributionChart below.

  const series: any[] = [];

  // Multi-line mode (attraction overlay)
  if (lines && lines.length > 0) {
    const legendNames = lines.map((l) => l.attractionName || l.attractionId);
    const allTimestamps = new Set<string>();
    lines.forEach((l) => l.data.forEach((d) => allTimestamps.add(d.timestamp)));
    const sortedTimes = Array.from(allTimestamps).sort();

    series.push({
      name: 'Queue Time (min)',
      type: 'line' as const,
      data: sortedTimes.map((t) => {
        const val = lines[0].data.find((d) => d.timestamp === t)?.waitTimeMinutes ?? null;
        return val !== undefined ? [t, val] : null;
      }),
      smooth: true,
      showSymbol: true,
      lineStyle: { width: 2, color: LINE_COLORS[0] },
    });

    lines.slice(1).forEach((line, idx) => {
      const lineColor = LINE_COLORS[(idx + 1) % LINE_COLORS.length];
      series.push({
        name: line.attractionName || line.attractionId,
        type: 'line' as const,
        data: sortedTimes.map((t) => {
          const val = line.data.find((d) => d.timestamp === t)?.waitTimeMinutes ?? null;
          return val !== undefined ? [t, val] : null;
        }),
        smooth: true,
        showSymbol: true,
        lineStyle: { width: 2, color: lineColor },
      });
    });

    return {
      title: { text: title, left: 'center' },
      tooltip: { trigger: 'axis' as const },
      legend: { data: legendNames, top: 0 },
      grid: { left: '3%', right: '4%', bottom: '3%', top: '60', containLabel: true },
      xAxis: {
        type: 'time' as const,
        axisLabel: {
          formatter: (value: number) => {
            const d = new Date(value);
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return months[d.getMonth()] + ' ' + String(d.getDate()).padStart(2, '0');
          },
        },
      },
      yAxis: { type: 'value' as const, name: 'Wait (min)', nameGap: 48, nameTextStyle: { fontSize: 12, fontWeight: 500 } },
      series,
    };
  }

  // Single-line mode (legacy data prop)
  if (data && data.length > 0) {
    series.push({
      name: 'Queue Time (min)',
      type: 'line' as const,
      data: data.map((d) => [d.timestamp, d.waitTimeMinutes]),
      smooth: true,
      showSymbol: true,
      lineStyle: { width: 2 },
    });
  }

  const legendData: string[] = lines && lines.length > 0
    ? lines.map((l) => l.attractionName || l.attractionId)
    : ['Queue Time (min)'];

  return {
    title: { text: title, left: 'center' },
    tooltip: { trigger: 'axis' as const },
    legend: { data: legendData, top: 0 },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '60', containLabel: true },
    xAxis: {
      type: 'time' as const,
      axisLabel: {
        formatter: (value: number) => {
          const d = new Date(value);
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return months[d.getMonth()] + ' ' + String(d.getDate()).padStart(2, '0');
        },
      },
    },
    yAxis: { type: 'value' as const, name: 'Wait (min)', nameGap: 48, nameTextStyle: { fontSize: 12, fontWeight: 500 } },
    series,
  };
}

export function TimeSeriesChart({
  data,
  lines,
  thresholds,
  singleThreshold,
  isLoading = false,
  title = 'Queue Time Trend',
}: TimeSeriesChartProps): React.ReactElement {
  if (isLoading) {
    return <div>Loading...</div>;
  }

  const option = getEChartsOption(data, lines, thresholds, singleThreshold, title);

  return (
    <div>
      <ReactECharts option={option} style={{ height: '400px' }} />
    </div>
  );
}
